import React, { useEffect, useRef, useState } from 'react';
import Head from 'next/head';
import ProgressBar from '../../components/ProgressBar';
import BradCrumb from '../../components/BradCrumb';
import st from './index.module.scss';
import NextToRead from '../../components/sliders/NextToRead';

import FacebookShareButton from '../../components/share/FacebookShareButton';
import LinkedInSHareButton from '../../components/share/LinkedInSHareButton';
import TwitterShareButton from '../../components/share/TwitterShareButton';
import EmailShareButton from '../../components/share/EmailShareButton';
import Preloader from '../../components/Preloader/Preloader';
import { parse } from 'node-html-parser';

import { toLocal, sanitizeId } from '../../utils/blogPost';
import ViewMoreBlog from '../../components/ViewMoreBlog';

const BlogPost = ({ data }) => {
  const [isMounted, setIsMounted] = useState(false);
  const [currentUrl, setCurrentUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandMenu, setExpandMenu] = useState(false);
  const sideBar = useRef(null);
  const sideBarMob = useRef(null);

  useEffect(() => {
    if (!isMounted && !!window) {
      setIsMounted(true);

      setTimeout(() => {
        if (sideBar.current) {
          const div = sideBar.current;
          div.style.height = div.scrollHeight + 'px';
        }
      }, 1000);
    }
  }, []);

  useEffect(() => {
    document.body.classList.remove('overflow-x-hidden');
    const stickyElement = document.getElementById('stickyMod');

    const handleScroll = () => {
      const initialOffsetTop = stickyElement.getBoundingClientRect().top;

      const lastEl = document.querySelector('div[data="last-el"]');

      if (window.scrollY >= initialOffsetTop + 100 && window.scrollY < lastEl.offsetTop - 100) {
        if (sideBarMob.current) {
          sideBarMob.current.parentElement.classList.remove('h-[95px]');
          sideBarMob.current.parentElement.classList.add('h-0');
        }

        stickyElement.classList.add('stickyM');
      } else {
        stickyElement.classList.remove('stickyM');
        if (sideBarMob.current) {
          sideBarMob.current.parentElement.classList.add('h-[95px]');
          sideBarMob.current.parentElement.classList.remove('h-0');
        }
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToHash = (selector, size = 'desc') => {
    const sanitizedSelector = sanitizeId(selector);
    const element = document.getElementById(sanitizedSelector);

    if (window.innerWidth < 1024) {
      setExpandMenu(false);
    }
    document.querySelectorAll(`.${st['scroll-target-desc']}`).forEach(el => el.classList.remove(st['scroll-target-desc']));
    document.querySelectorAll(`.${st['scroll-target-mob']}`).forEach(el => el.classList.remove(st['scroll-target-mob']));

    if (element) {
      element.classList.add(st[`scroll-target-${size}`]);
      element.scrollIntoView({ block: 'start', behavior: 'smooth', inline: 'start' });
    } else {
      const headings = document.querySelectorAll('h3');
      headings.forEach(heading => {
        if (heading.textContent === selector) {
          heading.classList.add(st[`scroll-target-${size}`]);
          heading.scrollIntoView({ block: 'start', behavior: 'smooth', inline: 'start' });
        }
      });
    }
  };

  const generateContent = (content, id) => {
    return <div id={id} dangerouslySetInnerHTML={{ __html: content }} />;
  };

  const generateSubTitles = (content, size = 'desc') => {
    const root = parse(content);
    const elements = root.querySelectorAll('h3');
    const titles = Array.from(elements).map(el => el.textContent);

    return titles?.map((title, idx) => (
      <p
        onClick={() => scrollToHash(title, size)}
        className={'flex mt-14 ml-[20px] items-center gap-4 text-14 leading-17 cursor-pointer ' + st['h3-before']}
        key={'subheading_' + idx}
      >
        {title}
      </p>
    ));
  };

  useEffect(() => {
    setCurrentUrl(window.location.href);
  }, []);

  return (
    <>
      {loading && <Preloader />}
      <Head>
        <title>{data.post.meta_title}</title>
        <link rel="alternate" hrefLang="en" href={currentUrl} />
        <meta name="description" content={data.post.meta_description} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={data.post.meta_title} />
        <meta property="og:description" content={data.post.meta_description} />
        <meta property="og:image" content={`${process.env.NEXT_PUBLIC_IMAGE_PATH}${data.post.og_image}`} />
      </Head>
      <main>
        {data.post.slug === 'how-to-choose-digital-marketing-agency-in-armenia' && (
          <script
            type="application/ld+json"
            className="organization-schema"
            dangerouslySetInnerHTML={{
              __html: `{
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://weprodigi.com/#org",
      "name": "Prodigi",
      "url": "https://weprodigi.com",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.7",
        "ratingCount": 29,
        "reviewCount": 29
      }
    },
    {
      "@type": "Review",
      "@id": "https://weprodigi.com/#review-1",
      "itemReviewed": { "@id": "https://weprodigi.com/#org" },
      "author": { "@type": "Person", "name": "Ani K." },
      "datePublished": "2025-06-18",
      "reviewBody": "Prodigi helped us scale SEO and PPC with clear reporting and fast execution.",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": "4.9",
        "bestRating": "5"
      }
    },
    {
      "@type": "Review",
      "@id": "https://weprodigi.com/#review-2",
      "itemReviewed": { "@id": "https://weprodigi.com/#org" },
      "author": { "@type": "Person", "name": "Levon M." },
      "datePublished": "2025-07-02",
      "reviewBody": "Great communication, creative social campaigns, and measurable results.",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": "4.7",
        "bestRating": "5"
      }
    },
    {
      "@type": "Review",
      "@id": "https://weprodigi.com/#review-3",
      "itemReviewed": { "@id": "https://weprodigi.com/#org" },
      "author": { "@type": "Person", "name": "Mariam S." },
      "datePublished": "2025-07-25",
      "reviewBody": "Thorough strategy, quality creatives, and a noticeable lift in ROI.",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": "4.8",
        "bestRating": "5"
      }
    },
    {
      "@type": "ImageObject",
      "@id": "https://weprodigi.com/blog/how-to-choose-digital-marketing-agency-in-armenia/#primaryimage",
      "url": "https://blog-admin.weprodigi.com/storage/uploads/image_6895ec4ceb126.jpeg"
    },
    {
      "@type": "VideoObject",
      "@id": "https://weprodigi.com/blog/how-to-choose-digital-marketing-agency-in-armenia/#video",
      "name": "Digital Marketing Agency in Armenia - Prodigi",
      "description": "As the biggest digital marketing agency in Armenia, Prodigi talks about the industry's evolution and shares insights into digital marketing.",
      "thumbnailUrl": "https://i.ytimg.com/vi/JLl5X_GB6t4/hqdefault.jpg",
      "embedUrl": "https://www.youtube.com/watch?v=JYGIfkCIsC0",
      "uploadDate": "2025-08-12T00:00:00+04:00"
    },
    {
      "@type": "BreadcrumbList",
      "@id": "https://weprodigi.com/blog/how-to-choose-digital-marketing-agency-in-armenia/#breadcrumb",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://weprodigi.com/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Blog",
          "item": "https://weprodigi.com/blog/"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "Digital Marketing Agency in Armenia: Navigating the Progressive Landscape",
          "item": "https://weprodigi.com/blog/how-to-choose-digital-marketing-agency-in-armenia/"
        }
      ]
    },
    {
      "@type": "BlogPosting",
      "@id": "https://weprodigi.com/blog/how-to-choose-digital-marketing-agency-in-armenia/#post",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://weprodigi.com/blog/how-to-choose-digital-marketing-agency-in-armenia/"
      },
      "url": "https://weprodigi.com/blog/how-to-choose-digital-marketing-agency-in-armenia/",
      "headline": "Digital Marketing Agency in Armenia: Navigating the Progressive Landscape",
      "description": "Looking for a top digital marketing agency in Armenia? Prodigi helps you succeed with customized SEO, PPC, web design, and social media strategies.",
      "datePublished": "2025-08-07",
      "dateModified": "2025-08-07",
      "inLanguage": "en",
      "articleSection": "Digital Marketing",
      "keywords": [
        "digital marketing agency Armenia",
        "SEO",
        "PPC",
        "social media marketing",
        "web design"
      ],
      "author": { "@type": "Person", "name": "Arev Khamoyan" },
      "publisher": {
        "@type": "Organization",
        "@id": "https://weprodigi.com/#org"
      },
      "image": {
        "@id": "https://weprodigi.com/blog/how-to-choose-digital-marketing-agency-in-armenia/#primaryimage"
      },
      "video": {
        "@id": "https://weprodigi.com/blog/how-to-choose-digital-marketing-agency-in-armenia/#video"
      },
      "mentions": [
        {
          "@type": "Service",
          "@id": "https://weprodigi.com/seo/professional-seo-services/#service",
          "name": "SEO Services"
        },
        {
          "@type": "Service",
          "@id": "https://weprodigi.com/paid-advertising-services/#service",
          "name": "Paid Advertising"
        },
        {
          "@type": "Service",
          "@id": "https://weprodigi.com/social-media-marketing/#service",
          "name": "Social Media Marketing"
        },
        {
          "@type": "Service",
          "@id": "https://weprodigi.com/video-production/#service",
          "name": "Video Production"
        },
        {
          "@type": "Service",
          "@id": "https://weprodigi.com/design/#service",
          "name": "Design"
        }
      ]
    }
  ]
}
`,
            }}
          />
        )}
        <div className="relative bg-white">
          <ProgressBar />
          <div id="main-contant" className="container">
            <div className="pt-32 ">
              <BradCrumb
                homeElement="Home"
                separator=">"
                containerClasses="hidden md:flex"
                listClasses="text-purple text-16 font-normal"
                activeClasses="active"
                capitalizeLinks={true}
              />
              <div className="flex flex-col-reverse flex-wrap items-center justify-between gap-6 mt-20 md:flex-row md:flex-nowrap">
                <div className="w-full md:w-[60%]">
                  <div className="title-block">
                    <h1 className={'leading-44 uppercase text-36 font-normal-700 max-w-450'}>{data.post.post_title}</h1>
                  </div>
                  <div className="flex gap-4 mt-8 time-and-date-info">
                    <div className="flex">
                      <img src="https://admin.weprodigi.com/uploads/images/blog/calendar.svg" width={16} alt="calendar" />
                      <span className={'text-12 leading-15 text-gray-dark ml-8'}>{toLocal(data.post?.created_at)}</span>
                    </div>

                    {data.post?.audio_duration && (
                      <div className="flex ">
                        <img src="https://admin.weprodigi.com/uploads/images/blog/clock.svg" width={16} alt="calendar" />
                        <span className={'text-12 leading-15 text-gray-dark ml-8'}>{data.post?.audio_duration} min. reading</span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-6 mt-24 md:flex-nowrap user">
                    <img
                      src={process.env.NEXT_PUBLIC_WEPRODIGI + data.post.user?.new_landing_avatar}
                      className={st.specialist_image}
                      width={64}
                      height={64}
                      alt="sepcialist avatar"
                    />
                    <div className={'max-w-200'}>
                      <p className={'font-bolder leading-22 text-18'}>{data.post.user?.name}</p>
                      <p className={'text-16 leading-20 font-b300'}>{data.post.user?.position}</p>
                    </div>
                    <div google-add-preferred-source-btn="" data-lang="en" data-theme="light" className="md:ml-auto"></div>
                  </div>
                </div>
                <BradCrumb
                  homeElement="Home"
                  separator=">"
                  containerClasses="self-start flex  md:hidden"
                  listClasses="text-purple text-16 font-medium"
                  activeClasses="active"
                  capitalizeLinks={true}
                />
                <div className="flex justify-end w-full md:w-[40%] pl-0 md:pl-[30px]">
                  <img src={process.env.NEXT_PUBLIC_IMAGE_PATH + data.post.photo} className={st.main_imag} alt="main_image" />
                </div>
              </div>
            </div>
            <div className="pb-32 mt-32 content">
              <div className="flex flex-wrap justify-between h-full md:flex-nowrap">
                <div className="w-full md:w-60p">
                  {/* Content Section */}
                  <p className="hidden md:block text-12 leading-15 text-[#262628]">
                    <span className="font-normal-700">Summary. </span>
                    <ViewMoreBlog ln={40}>{data.post.article}</ViewMoreBlog>
                  </p>
                  <div className="block md:hidden">
                    <span className="font-normal-700">Summary. </span>
                    <ViewMoreBlog ln={20}>{data.post?.article}</ViewMoreBlog>
                  </div>
                  <audio controls src={process.env.NEXT_PUBLIC_IMAGE_PATH + data.post.audio} id="audio-player" className={st.audio + ' mt-32'} />

                  {/* MobileMenu */}

                  <div className="block md:hidden h-[95px] mt-32 md:mt-0">
                    <div
                      id="stickyMod"
                      ref={sideBarMob}
                      className={`bg-purple-videoStyle w-full px-24 py-32 rounded-5 block md:hidden relative z-[900]`}
                    >
                      {data.post && (
                        <div className="flex justify-between" onClick={() => setExpandMenu(!expandMenu)}>
                          <h2 className="font-medium text-24 leading-29 text-gray-headerIcon">Table Of Contents</h2>
                          <img
                            src="https://weprodigi.com/uploads/images/blog/arrow_forward_ios.svg"
                            className={'block md:hidden ' + (expandMenu ? '' : st.rotate)}
                            width={24}
                            alt=""
                          />
                        </div>
                      )}
                      <div className={expandMenu ? '' : 'hidden ' + ' md:block'}>
                        <div className="w-full mt-12 border-white border-t-1"></div>
                        <div className="mt-14">
                          {data.post.details?.map((detail, ind) => (
                            <div key={'heading_' + ind}>
                              <h3
                                onClick={() => scrollToHash(`${detail?.title}`, 'mob')}
                                className={'flex mt-14 items-center gap-4 text-14 leading-17 cursor-pointer ' + st['h3-before']}
                              >
                                {detail.title}
                              </h3>
                              {isMounted && generateSubTitles(detail.content, 'mob')}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Content */}
                  <div className={`mt-32 ${st.content}`}>{generateContent(data.post.content, 'content')}</div>
                  <div className={st['details-part']}>
                    {data.post.details?.map((detail, ind) => (
                      <div
                        data={ind === data.post.details?.length - 1 ? 'last-el' : ''}
                        key={'detail' + ind}
                        id={sanitizeId(detail.title)}
                        className="detail_row md:block"
                      >
                        <h2>{detail.title}</h2>
                        {generateContent(detail.content, '')}
                      </div>
                    ))}
                  </div>
                  {/* Share Section */}
                  <div className="block w-full max-w-full mt-32 md:max-w-400 md:hidden">
                    <h2 className="text-center text-24 leading-24 font-normal-bold md:text-left">Share this article</h2>
                    <div className="flex justify-center gap-6 mt-24 md:justify-start">
                      <FacebookShareButton url={currentUrl} quote={data.post.post_title} />
                      <LinkedInSHareButton
                        url={currentUrl}
                        title={data.post.post_title}
                        summary={data.post.article}
                        source={process.env.NEXT_PUBLIC_WEPRODIGI + data.post.photo}
                      />
                      <TwitterShareButton url={currentUrl} text={data.post.post_title} hashtags="" via="" />
                      <EmailShareButton subject={data.post.post_title} body={`Check out this site ${currentUrl}`} />
                    </div>
                  </div>
                </div>
                {data.post && (
                  <div
                    ref={sideBar}
                    className={`w-full md:w-40p mt-32 md:mt-0 hidden md:flex flex-col justify-start self-start items-end ${st['stickyBar']}`}
                  >
                    <div className="w-full px-24 py-32 frame bg-purple-videoStyle max-w-400 rounded-5 ">
                      <div className="flex justify-between">
                        <h2 className="font-medium text-24 leading-29 text-gray-headerIcon">Table Of Contents</h2>
                        <img
                          src="https://weprodigi.com/uploads/images/blog/arrow_forward_ios.svg"
                          className={'block md:hidden ' + (expandMenu ? '' : st.rotate)}
                          onClick={() => setExpandMenu(!expandMenu)}
                          width={24}
                          alt="check mark"
                        />
                      </div>
                      <div className={expandMenu ? '' : 'hidden ' + ' md:block'}>
                        <div className="w-full mt-12 border-white border-t-1"></div>
                        <div className="mt-14">
                          {data.post.details?.map((detail, ind) => (
                            <div key={'heading_' + ind}>
                              <h3
                                onClick={() => scrollToHash(detail?.title)}
                                className={'flex mt-14 items-center gap-4 text-14 leading-17 cursor-pointer ' + st['h3-before']}
                              >
                                {detail.title}
                              </h3>
                              {isMounted && generateSubTitles(detail.content)}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="w-full max-w-full mt-32 md:max-w-400 ">
                      <h2 className="text-center text-24 leading-24 font-normal-bold md:text-left">Share this article</h2>
                      <div className="flex justify-center gap-6 mt-24 md:justify-start">
                        <FacebookShareButton url={currentUrl} quote={data.post.post_title} />
                        <LinkedInSHareButton
                          url={currentUrl}
                          title={data.post.post_title}
                          summary={data.post.article}
                          source={process.env.NEXT_PUBLIC_WEPRODIGI + data.post.photo}
                        />
                        <TwitterShareButton url={currentUrl} text={data.post.post_title} hashtags="" via="" />
                        <EmailShareButton subject={data.post.post_title} body={`Check out this site ${currentUrl}`} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="contaniner-fluid md:container relative z-[3]">
            <div className={`h-[220px] md:h-[260px] w-full lg:w-60p bg-[#000000] overflow-hidden md:rounded-5 relative ${st['blog-banner']}`}>
              <a
                href="https://weprodigi.com/contact-us"
                className="absolute z-10 flex items-center justify-center h-40 py-0 font-normal text-black transition-all duration-300 -translate-x-1/2 bg-white shadow-md cursor-pointer text-18 leading-24 rounded-5 px-23 md:py-5 hover:bg-green-badge hover:text-white whitespace-nowrap bottom-20 left-1/2 md:bottom-auto md:left-auto md:right-[72px] md:top-1/2 md:-translate-y-1/2 md:translate-x-0"
              >
                Contact Us
              </a>
            </div>
          </div>
          <div className="container pb-32">
            {data.next_to_read && (
              <>
                <h2 className={'mt-82 mb-20 text-34 leading-41 font-bolder'}>What to read next</h2>
                <NextToRead items={data.next_to_read} />
              </>
            )}
          </div>
        </div>
      </main>
    </>
  );
};
export default BlogPost;

export async function getStaticPaths() {
  // Only pre-render a small subset of paths. Other paths will be rendered at runtime.
  return {
    paths: [],
    fallback: 'blocking',
  };
}

export async function getStaticProps({ params }) {
  const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}blog/${params.post}`);

  if (!res.ok) {
    console.error(`Error fetching post: ${res.status} ${res.statusText}`);
    return { notFound: true };
  }

  const data = await res.json();

  return {
    props: {
      data,
    },
    revalidate: 10,
  };
}
